import React from 'react';
import { Alert, Card, Form } from 'react-bootstrap';

import { Waiting } from '../../../common/components/Waiting/Waiting';
import { usePokCore } from '../../../common/hooks/usePokCore';
import {
  GetClientDto,
  PreparedGoogleRowDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import FormRow from '../../../app/components/FormRow';
import { ClientSelector } from '../../components/Selects/ClientSelector';
import { FileSelector } from '../../components/Attachments/FileSelector';
import ValidationAlert from '../../../app/components/ValidationAlert';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { useTranslation } from '../../../common/hooks/useTranslation';
import CompanySelector from '../../components/Selects/CompanySelector';

import { usePurchaseInvoiceImport } from './usePurchaseInvoiceImport';

export const PurchasesInvoicesImportView: React.FC = () => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('fvzLng');

  const prepareRows = (fileContent: string, clientId: string) =>
    pok.purchaseInvoices.prepareGoogleImport({ fileContent, clientId });

  const processRow = async (
    row: PreparedGoogleRowDto,
    file: File,
    clientId: string,
    companyId: string,
  ) => {
    const attachmentContent = Buffer.from(await file.arrayBuffer()).toString(
      'base64',
    );
    await pok.purchaseInvoices.createGoogleImportRow({
      invoiceNumber: row.invoiceNumber,
      currency: row.currency,
      issueDate: row.issueDate,
      sellDate: row.sellDate,
      paymentDate: row.paymentDate,
      amount: row.amount,
      exchangeRate: row.exchangeRate,
      type: row.type,
      clientId,
      companyId,
      projectNumber: row.projectNumber ?? null,
      teamsNames: row.teamsNames ?? null,
      comment: row.comment,
      attachmentContent,
      attachmentName: file.name,
    });
  };

  const {
    fileInputRef,
    fileNameRef,
    directoryInputRef,
    directoryNameRef,
    showNewAlert,
    showAlert,
    variant,
    waiting,
    alert,
    excel,
    directory,
    client,
    setClient,
    handleFileChange,
    handleDirectoryChange,
    handleSearch,
  } = usePurchaseInvoiceImport(prepareRows, processRow);

  return (
    <>
      <Card className="mb-2">
        <Card.Header>{t(tk.finance.googleImport)}</Card.Header>
        <Card.Body>
          <Alert variant="danger" className="mt-3">
            {t(tk.finance.userInCompanyContext)}
          </Alert>
          <Alert variant="info" className="mt-3">
            {t(tk.finance.assumptions)}:
            <ul className="alert-list">
              <li>{t(tk.finance.assumption1)}</li>
              <li>{t(tk.finance.assumptionColumnOrder)}</li>
              <li>{t(tk.finance.assumption2)}</li>
              <li>{t(tk.finance.assumption3)}</li>
              <li>{t(tk.finance.assumption4)}</li>
              <li>{t(tk.finance.assumptionProjectsNumber)}</li>
              <li>{t(tk.finance.assumptionTeamsNames)}</li>
            </ul>
          </Alert>
          {!waiting && (
            <>
              <Form className="d-grid gap-3">
                <FormRow controlId="client" label={t(tk.common.company)}>
                  <CompanySelector value={pok.getCompanyId()} readOnly={true} />
                </FormRow>
                <FormRow controlId="client" label={t(tk.finance.client)}>
                  <ClientSelector
                    onChange={option =>
                      setClient({
                        id: option.value,
                        name: option.label,
                      } as GetClientDto)
                    }
                    value={client}
                  />
                </FormRow>
                <FormRow
                  controlId="excel"
                  label={t(tk.finance.excelWithGoogleInvoices)}
                >
                  <FileSelector
                    fileInputRef={fileInputRef}
                    fileNameRef={fileNameRef}
                    value={excel}
                    handleFileChange={handleFileChange}
                    accept=".xlsx"
                  />
                </FormRow>
                <FormRow
                  controlId="directory"
                  label={t(tk.finance.directoryWithGoogleInvoices)}
                >
                  <FileSelector
                    fileInputRef={directoryInputRef}
                    fileNameRef={directoryNameRef}
                    value={directory}
                    handleFileChange={handleDirectoryChange}
                    accept=".xlsx"
                    isDirectory={true}
                    buttonText={t(tk.finance.chooseDirectory)}
                  />
                </FormRow>
              </Form>
              {showNewAlert && (
                <Alert variant="danger" className="mt-3">
                  {t(tk.finance.errorSelectData)}
                </Alert>
              )}
              <div className="d-flex justify-content-end mt-3">
                <ConfirmationButton
                  variant="outline-primary"
                  onOK={handleSearch}
                  confirmation={t(tk.finance.askImport)}
                  isLoading={waiting}
                >
                  {t(tk.finance.import)}
                </ConfirmationButton>
              </div>
            </>
          )}
          {waiting && <Waiting />}
        </Card.Body>
      </Card>
      <ValidationAlert
        variant={variant}
        show={showAlert}
        errors={alert}
        className="m-3"
      />
    </>
  );
};
